import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import {
  Button,
  CircularProgress,
  IconButton,
  Snackbar,
  SnackbarContent,
  Tooltip,
  Typography,
} from "@material-ui/core";
import InfoOutlined from "@material-ui/icons/InfoOutlined";
import "./ForageCycleManagementPage.css";
import { t } from "adapters/i18n";
import ForragemService from "services/ForragemService";
import ForragemCycleService from "services/ForragemCycleService";
import PastoService from "services/PastoService";
import { exibirCarregando } from "redux/actions/loadingAction";
import type { RootState } from "redux/reducers";
import ForageCycleCard from "./components/ForageCycleManagement/ForageCycleCard";
import ForageCycleDialogs from "./components/ForageCycleManagement/ForageCycleDialogs";
import ForageCycleManagementFooter from "./components/ForageCycleManagement/ForageCycleManagementFooter";
import {
  ForageCycleEmptyState,
  ForageCycleLoadError,
} from "./components/ForageCycleManagement/ForageCycleListStates";
import type { EditablePeriodField } from "./components/ForageCycleManagement/ForageCyclePeriodRow";
import {
  createEditablePeriod,
  type CycleValidationError,
  type EditableCyclePeriod,
  type EntityId,
  type ForageCyclePayload,
  type ForageCycleRecord,
  listCyclesWithActivePastureCounts,
  type PastureRecord,
  toCyclePayloadPeriods,
  validateCyclePeriods,
} from "../forageCycleManagementUtils";

interface ForageCycleDraft {
  cycle: ForageCycleRecord;
  dirty: boolean;
  periods: EditableCyclePeriod[];
}

interface CycleSaveRequest {
  cycle: ForageCycleRecord;
  payload: ForageCyclePayload;
}

type DraftValidationError = CycleValidationError & { cycleKey: string };

function createEmptyPeriod(): EditableCyclePeriod {
  return {
    dataInicio: "",
    dataFim: "",
    AlturaEntrada: "",
    AlturaSaida: "",
    QuantidadeDiasDescanso: "",
    Rotulo: "",
  };
}

function createCycleDraft(cycle: ForageCycleRecord): ForageCycleDraft {
  const periods = Array.isArray(cycle.PeriodoCicloForragemFazenda)
    ? cycle.PeriodoCicloForragemFazenda.map(createEditablePeriod)
    : [];
  return { cycle, periods, dirty: false };
}

function getCycleKey(cycle: ForageCycleRecord): string {
  return `${Number(cycle.IdFazenda)}:${Number(cycle.IdForragem)}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function getProblemMessage(error: unknown): string {
  const response =
    isRecord(error) && isRecord(error.response) ? error.response : null;
  const headers =
    response && isRecord(response.headers) ? response.headers : null;
  const contentType =
    headers && typeof headers["content-type"] === "string"
      ? headers["content-type"]
      : null;
  const problem = response && isRecord(response.data) ? response.data : null;
  if (
    contentType &&
    contentType.includes("application/problem+json") &&
    problem &&
    typeof problem.title === "string"
  ) {
    return problem.title;
  }
  if (problem && typeof problem.mensagem === "string") {
    return problem.mensagem;
  }
  return t("forageCycle:cycle-save-error");
}

function getServerFieldErrors(
  error: unknown,
): Array<{ fieldLabel: string; message: string }> {
  const response =
    isRecord(error) && isRecord(error.response) ? error.response : null;
  const payload = response && isRecord(response.data) ? response.data : null;
  const fields = payload && Array.isArray(payload.campos) ? payload.campos : [];
  const fieldLabelKeys: Record<string, string> = {
    AlturaEntrada: "cycle-entry-height",
    AlturaSaida: "cycle-exit-height",
    "AlturaEntrada/AlturaSaida": "cycle-heights",
    DiaFim: "cycle-end-date",
    DiaInicio: "cycle-start-date",
    MesFim: "cycle-end-date",
    MesInicio: "cycle-start-date",
    PeriodoCicloForragemFazenda: "cycle-periods",
    QuantidadeDiasDescanso: "cycle-rest-days",
    Rotulo: "cycle-period-label",
  };

  return fields.reduce<Array<{ fieldLabel: string; message: string }>>(
    (errors, field) => {
      if (
        !isRecord(field) ||
        typeof field.campo !== "string" ||
        typeof field.mensagem !== "string"
      ) {
        return errors;
      }
      const fieldLabelKey = fieldLabelKeys[field.campo];
      errors.push({
        fieldLabel: fieldLabelKey
          ? t(`forageCycle:${fieldLabelKey}`)
          : field.campo,
        message: field.mensagem,
      });
      return errors;
    },
    [],
  );
}

function isPastureRecord(value: unknown): value is PastureRecord {
  if (!isRecord(value)) {
    return false;
  }
  const hasFarmId =
    typeof value.IdFazenda === "number" || typeof value.IdFazenda === "string";
  const hasForageId =
    value.IdForragem === null ||
    typeof value.IdForragem === "number" ||
    typeof value.IdForragem === "string";
  const hasValidDeletedFlag =
    value.Excluido === undefined || typeof value.Excluido === "boolean";
  return hasFarmId && hasForageId && hasValidDeletedFlag;
}

function readPastures(response: unknown): PastureRecord[] {
  if (!isRecord(response) || !Array.isArray(response.data)) {
    return [];
  }
  return response.data.filter(isPastureRecord);
}

interface ForageNameRecord {
  IdForragem: EntityId;
  Nome: string;
}

function isForageNameRecord(value: unknown): value is ForageNameRecord {
  return (
    isRecord(value) &&
    (typeof value.IdForragem === "number" ||
      typeof value.IdForragem === "string") &&
    typeof value.Nome === "string"
  );
}

function readForages(response: unknown): ForageNameRecord[] {
  if (!isRecord(response) || !Array.isArray(response.data)) {
    return [];
  }
  return response.data.filter(isForageNameRecord);
}

function ForageCycleManagementPage({
  farmId,
  setGlobalLoading,
}: {
  farmId: EntityId | null;
  setGlobalLoading: (isLoading: boolean) => void;
}) {
  const [drafts, setDrafts] = useState<ForageCycleDraft[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [reloadCount, setReloadCount] = useState(0);
  const [validatedCycleKeys, setValidatedCycleKeys] = useState<string[]>([]);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [serverFieldErrors, setServerFieldErrors] = useState<
    Array<{
      cycleKey: string;
      errors: Array<{ fieldLabel: string; message: string }>;
    }>
  >([]);
  const [failedRequests, setFailedRequests] = useState<CycleSaveRequest[]>([]);
  const [successMessage, setSuccessMessage] = useState("");
  const inFlight = useRef(false);
  const farmContextVersion = useRef(0);

  const validationErrors: DraftValidationError[] = [];
  drafts.forEach((draft) => {
    if (!draft.dirty) return;
    validateCyclePeriods(draft.periods).forEach((error) => {
      validationErrors.push({ ...error, cycleKey: getCycleKey(draft.cycle) });
    });
  });

  useEffect(() => {
    let currentRequest = true;
    farmContextVersion.current += 1;
    setDrafts([]);
    setValidatedCycleKeys([]);
    setConfirmationOpen(false);
    setErrorDialogOpen(false);
    setSaveError("");
    setServerFieldErrors([]);
    setFailedRequests([]);
    setSuccessMessage("");
    if (farmId == null) {
      setLoadError(false);
      setLoading(false);
      return () => {
        currentRequest = false;
      };
    }

    setLoading(true);
    setLoadError(false);
    Promise.all([
      ForragemCycleService.obterCiclosForragemFazendaV2(farmId),
      PastoService.listaPastosPorIdFazenda(farmId),
    ])
      .then(async ([farmCycles, pastureResponse]) => {
        if (!currentRequest) return;
        const pastures = readPastures(pastureResponse);
        const listedCycles = listCyclesWithActivePastureCounts(
          farmCycles,
          pastures,
          farmId,
        );
        const forageIds = Array.from(
          new Set(listedCycles.map((cycle) => Number(cycle.IdForragem))),
        );
        const forageResponse = forageIds.length
          ? await ForragemService.obterForragensPorIds(forageIds)
          : null;
        if (!currentRequest) return;
        const forageNames = new Map<number, string>();
        readForages(forageResponse).forEach((forage) => {
          forageNames.set(Number(forage.IdForragem), forage.Nome);
        });
        setDrafts(
          listedCycles.map((cycle) =>
            createCycleDraft({
              ...cycle,
              nomeForragem: forageNames.get(Number(cycle.IdForragem)),
            }),
          ),
        );
      })
      .catch(() => {
        if (currentRequest) {
          setDrafts([]);
          setLoadError(true);
        }
      })
      .finally(() => {
        if (currentRequest) setLoading(false);
      });

    return () => {
      currentRequest = false;
    };
  }, [farmId, reloadCount]);

  const clearSaveFeedback = () => {
    setSaveError("");
    setServerFieldErrors([]);
    setFailedRequests([]);
    setErrorDialogOpen(false);
    setSuccessMessage("");
  };

  const updatePeriod = (
    cycle: ForageCycleRecord,
    index: number,
    field: EditablePeriodField,
    value: string,
  ) => {
    const selectedCycleKey = getCycleKey(cycle);
    setDrafts((current) =>
      current.map((draft) =>
        getCycleKey(draft.cycle) === selectedCycleKey
          ? {
              ...draft,
              dirty: true,
              periods: draft.periods.map((period, periodIndex) =>
                periodIndex === index ? { ...period, [field]: value } : period,
              ),
            }
          : draft,
      ),
    );
    clearSaveFeedback();
  };

  const addPeriod = (cycle: ForageCycleRecord) => {
    const selectedCycleKey = getCycleKey(cycle);
    setDrafts((current) =>
      current.map((draft) => {
        if (
          getCycleKey(draft.cycle) !== selectedCycleKey ||
          draft.periods.length >= 4
        ) {
          return draft;
        }
        return {
          ...draft,
          dirty: true,
          periods: [...draft.periods, createEmptyPeriod()],
        };
      }),
    );
    setValidatedCycleKeys((current) =>
      current.filter((cycleKey) => cycleKey !== selectedCycleKey),
    );
    clearSaveFeedback();
  };

  const removePeriod = (cycle: ForageCycleRecord, index: number) => {
    const selectedCycleKey = getCycleKey(cycle);
    setDrafts((current) =>
      current.map((draft) => {
        if (
          getCycleKey(draft.cycle) !== selectedCycleKey ||
          draft.periods.length <= 1
        ) {
          return draft;
        }
        return {
          ...draft,
          dirty: true,
          periods: draft.periods.filter(
            (_, periodIndex) => periodIndex !== index,
          ),
        };
      }),
    );
    clearSaveFeedback();
  };

  const createSaveRequests = (): CycleSaveRequest[] =>
    drafts
      .filter((draft) => draft.dirty && draft.periods.length > 0)
      .map((draft) => {
        if (farmId == null) {
          throw new Error(
            "Não é possível salvar ciclos sem fazenda selecionada.",
          );
        }
        return {
          cycle: draft.cycle,
          payload: {
            IdFazenda: Number(farmId),
            IdForragem: draft.cycle.IdForragem,
            PeriodoCicloForragemFazenda: toCyclePayloadPeriods(draft.periods),
          },
        };
      });

  const saveRequests = async (requests: CycleSaveRequest[]) => {
    if (inFlight.current || !requests.length) return;
    const submittedContextVersion = farmContextVersion.current;
    let updatedDrafts = drafts;
    inFlight.current = true;
    setGlobalLoading(true);
    setSaving(true);
    setSaveError("");
    setServerFieldErrors([]);
    setFailedRequests([]);
    setConfirmationOpen(false);
    setErrorDialogOpen(false);

    try {
      for (let index = 0; index < requests.length; index += 1) {
        const request = requests[index];
        try {
          const savedCycle =
            request.cycle.IdCicloForragemFazenda == null
              ? await ForragemCycleService.criarCicloForragemFazendaV2(
                  request.payload,
                )
              : await ForragemCycleService.atualizarCicloForragemFazendaV2(
                  request.cycle.IdCicloForragemFazenda,
                  request.payload,
                );
          if (farmContextVersion.current !== submittedContextVersion) return;

          const savedPeriods = savedCycle.PeriodoCicloForragemFazenda;
          if (
            savedCycle.IdCicloForragemFazenda == null ||
            !Array.isArray(savedPeriods)
          ) {
            throw new Error("Resposta inválida ao salvar o ciclo.");
          }

          const updatedCycle = {
            ...savedCycle,
            quantidadePastosAtivos: request.cycle.quantidadePastosAtivos,
          };
          updatedDrafts = updatedDrafts.map((draft) =>
            getCycleKey(draft.cycle) === getCycleKey(updatedCycle)
              ? createCycleDraft(updatedCycle)
              : draft,
          );
          setDrafts(updatedDrafts);
        } catch (error) {
          if (farmContextVersion.current === submittedContextVersion) {
            setFailedRequests(requests.slice(index));
            setSaveError(getProblemMessage(error));
            const fieldErrors = getServerFieldErrors(error);
            setServerFieldErrors(
              fieldErrors.length
                ? [
                    {
                      cycleKey: getCycleKey(request.cycle),
                      errors: fieldErrors,
                    },
                  ]
                : [],
            );
            setErrorDialogOpen(true);
          }
          return;
        }
      }

      setFailedRequests([]);
      setSaveError("");
      setServerFieldErrors([]);
      setValidatedCycleKeys([]);
      setSuccessMessage(t("forageCycle:cycle-save-success"));
    } finally {
      inFlight.current = false;
      setGlobalLoading(false);
      setSaving(false);
    }
  };

  const handleSave = () => {
    const dirtyDrafts = drafts.filter((draft) => draft.dirty);
    setValidatedCycleKeys(dirtyDrafts.map((draft) => getCycleKey(draft.cycle)));
    if (!dirtyDrafts.length || validationErrors.length) return;
    setConfirmationOpen(true);
  };

  const confirmSave = () => {
    if (saving || validationErrors.length) return;
    const requests = createSaveRequests();
    setConfirmationOpen(false);
    if (!requests.length) {
      setSuccessMessage(t("forageCycle:cycle-no-changes"));
      return;
    }
    void saveRequests(requests);
  };

  const getPeriodsError = (cycle: ForageCycleRecord) => {
    const cycleKey = getCycleKey(cycle);
    if (!validatedCycleKeys.includes(cycleKey)) return "";
    const error = validationErrors.find(
      (item) => item.cycleKey === cycleKey && item.field === "periods",
    );
    return error ? t(`forageCycle:${error.messageKey}`) : "";
  };

  const forageCountLabel =
    drafts.length === 1
      ? t("forageCycle:cycle-active-forage-count-one")
      : t("forageCycle:cycle-active-forage-count", { count: drafts.length });

  return (
    <div className="forage-cycle-management">
      <div className="forage-cycle-management__content">
        <Typography
          component="h1"
          style={{ fontSize: 14, fontWeight: 700, marginBottom: 12 }}
        >
          {t("forageCycle:cycle-management")}
        </Typography>

        {loading && (
          <div role="status" className="forage-cycle-management__loading">
            <CircularProgress
              aria-label={t("forageCycle:cycle-management-loading")}
            />
          </div>
        )}

        {!loading && loadError && (
          <ForageCycleLoadError
            onRetry={() => setReloadCount((count) => count + 1)}
          />
        )}

        {!loading && !loadError && drafts.length === 0 && (
          <ForageCycleEmptyState />
        )}

        {!loading && !loadError && drafts.length > 0 && (
          <section aria-labelledby="active-forage-heading">
            <div className="forage-cycle-management__section-heading">
              <Typography
                id="active-forage-heading"
                component="h2"
                style={{ fontSize: 12, fontWeight: 700, margin: 0 }}
              >
                {t("forageCycle:cycle-management-section")}
              </Typography>
              <Tooltip title={t("forageCycle:cycle-management-info")}>
                <IconButton
                  aria-label={t("forageCycle:cycle-management-info")}
                  style={{ padding: 3, marginLeft: 4 }}
                >
                  <InfoOutlined style={{ color: "#00569E", fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </div>
            <Typography
              component="p"
              style={{
                fontSize: 11,
                color: "#757575",
                marginTop: 2,
                marginBottom: 12,
                lineHeight: 1.3,
              }}
            >
              {t("forageCycle:cycle-management-helper")}
            </Typography>

            {saveError && !errorDialogOpen && (
              <Typography
                role="alert"
                style={{ color: "#D32F2F", fontSize: 11 }}
              >
                {saveError}{" "}
                {failedRequests.length > 0 && (
                  <Button
                    color="secondary"
                    onClick={() => setErrorDialogOpen(true)}
                    style={{ fontSize: 11, textTransform: "none" }}
                  >
                    {t("forageCycle:cycle-retry-save")}
                  </Button>
                )}
              </Typography>
            )}

            {drafts.map((draft) => {
              const { cycle, periods } = draft;
              const cycleKey = getCycleKey(cycle);
              const isValidationVisible = validatedCycleKeys.includes(cycleKey);
              const cycleTestId =
                cycle.IdCicloForragemFazenda == null
                  ? `cycle-card-new-${cycle.IdForragem}`
                  : `cycle-card-${cycle.IdCicloForragemFazenda}`;

              return (
                <ForageCycleCard
                  cycle={cycle}
                  cycleTestId={cycleTestId}
                  key={cycleKey}
                  periods={periods}
                  periodsError={getPeriodsError(cycle)}
                  saving={saving}
                  validationErrors={validationErrors.filter(
                    (error) =>
                      isValidationVisible && error.cycleKey === cycleKey,
                  )}
                  serverFieldErrors={
                    serverFieldErrors.find((item) => item.cycleKey === cycleKey)
                      ?.errors ?? []
                  }
                  onAddPeriod={() => addPeriod(cycle)}
                  onChangePeriod={(index, field, value) =>
                    updatePeriod(cycle, index, field, value)
                  }
                  onRemovePeriod={(index) => removePeriod(cycle, index)}
                />
              );
            })}
          </section>
        )}
      </div>

      <ForageCycleManagementFooter
        countLabel={forageCountLabel}
        saving={saving}
        disabled={
          saving || loading || loadError || !drafts.some((draft) => draft.dirty)
        }
        onSave={handleSave}
      />

      <ForageCycleDialogs
        confirmationOpen={confirmationOpen}
        drafts={drafts.filter((draft) => draft.dirty)}
        errorDialogOpen={errorDialogOpen}
        hasFailedRequests={failedRequests.length > 0}
        saveError={saveError}
        saving={saving}
        onCloseConfirmation={() => setConfirmationOpen(false)}
        onCloseError={() => setErrorDialogOpen(false)}
        onConfirmSave={confirmSave}
        onRetrySave={() => void saveRequests(failedRequests)}
      />

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={3500}
        onClose={() => setSuccessMessage("")}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <SnackbarContent
          role="status"
          message={successMessage}
          style={{
            backgroundColor: "#2E7D32",
            borderRadius: 4,
            fontSize: 12,
          }}
        />
      </Snackbar>
    </div>
  );
}

const mapStateToProps = (state: RootState) => ({
  farmId: state.fazenda.FazendaSelecionada,
});

const mapDispatchToProps = {
  setGlobalLoading: exibirCarregando,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(ForageCycleManagementPage);
